<template>
    <main class="flex-1 relative pb-8 z-0 overflow-y-auto">
        <PageHeader>
            <h1 class="ml-3 text-2xl font-bold text-gray-900 sm:truncate flex flex-row justify-between w-full">
                <span>
                    <span class="flex flex-row items-center">
                        <router-link :to="{ name: 'projects' }" class="text-cyan-800 hover:text-cyan-500">
                            Projets
                        </router-link>
                        <Loader v-if="isBuildingFetchPending" class="h-6 w-6 m-2 border-2 -red -sm"/>
                        <template v-else>
                            <svg-vue icon="angle-right-b" class="h-6 w-6 fill-current"></svg-vue>
                            <router-link v-if="project" :to="{name: 'project-show', params: {id: project.id}}" class="text-cyan-800 hover:text-cyan-500">
                                {{ project ? project.translatable.name[locale] : '' }}
                            </router-link>

                            <svg-vue icon="angle-right-b" class="h-6 w-6 fill-current"></svg-vue>
                            {{ building ? building.translatable.description[locale] : ''}}

                            <button type="button" @click.prevent="handleEditSettings">
                                <svg-vue icon="setting" class="h-8 w-8 text-custom-300" />
                            </button>
                        </template>
                    </span>
                    <LangSwitcher />
                </span>
                <span>
                    <a href="#" @click.prevent="handleCreateFloor"
                       class="flex items-center px-4 py-1 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-custom-600 hover:bg-custom-800 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-cyan-500">
                        Créer un étage
                    </a>
                </span>
            </h1>
        </PageHeader>

        <div class="mt-8 lg:max-w-6xl lg:mx-auto">
            <div class="m-4">
                <h2 class="mb-2 text-1xl font-bold text-gray-900 sm:truncate w-full">Étages</h2>
                <Search
                    v-model="search"
                    @update:modelValue="handleSearchChange"
                />

                <List
                    :items="floors"
                    :columns="columns"
                    :desc="desc"
                    @editItem="handleEditFloor"
                    @deleteItem="handleDeleteFloor"
                    @manageItem="handleManageFloor"
                    @sortBy="handleSortBy"
                    @dragEnd="handleDragEnd"
                    editable
                    deletable
                    manageable
                    moveable
                >
                    <template #actions="actionsProps">
                        <a v-if="actionsProps.item.adlead_id" href="/adlead" class="text-indigo-600 hover:text-indigo-900 px-2" @click.prevent="handleSync(actionsProps.item)">Sync. w/ Adlead</a>
                    </template>
                </List>

                <Pagination
                    v-if="lastPage > 1"
                    :current_project="currentPage"
                    :last_page="lastPage"
                    @change="handleChangePage"
                />
            </div>
        </div>
    </main>
</template>

<script>
import PageHeader from "../../components/partials/page-header";
import Loader from "../../components/partials/loader";
import LangSwitcher from "../../components/partials/lang-switcher";
import List from "../../components/partials/list"
import Pagination from "../../components/partials/pagination"
import Search from "../../components/partials/search"
import USER_ROLE from "../../enums/user-role";

export default {
    data() {
        return {
            search: null,
            desc: false
        }
    },
    beforeMount() {
        this.$store.dispatch('buildings/getSingle', {id: this.$route.params.id, includes: ['project','settings']})
        this.$store.dispatch('floors/getList', {search: {building_id: this.$route.params.id}})
    },
    computed: {
        isBuildingFetchPending() {
            return this.$store.state.buildings.isFetchSinglePending
        },
        building() {
            return this.$store.state.buildings.single;
        },
        project() {
            return this.building ? this.building.project : null;
        },
        locale() {
            return this.$store.state.config.locale;
        },
        floors: {
            get() {
                return this.$store.state.floors.list;
            },
            set(list) {
                this.$store.commit('floors/SET_LIST', list)
            }
        },
        lastPage() {
            return this.$store.state.floors.pagination.last_page
        },
        currentPage() {
            return this.$store.state.floors.pagination.current_page
        },
        columns() {
            return {
                name: {
                    sortable: false,
                    label: 'ID',
                    sortBy: 'name'
                },
                adlead_id: {
                    sortable: false,
                    label: 'ID AdLead',
                    sortBy: 'adlead_id'
                },
                ['translatable.description.'+this.locale]: {
                    sortable: false,
                    label: 'Nom'
                }
            }
        },
        authUser() {
            return this.$store.state.auth.auth_user;
        },
        isRoot() {
            if(this.authUser) {
                return Number(this.authUser.role) === Number(USER_ROLE.values.ROOT.description)
            }

            return false
        },
    },
    methods: {
        handleDeleteFloor(floor) {
            this.$store.state.floors.single = floor;
            this.$store.dispatch('modals/open', 'floor-delete');
        },
        handleChangePage(page = this.currentPage) {
            this.$store.dispatch('floors/getList', {
                'page': page,
                'sort_by': this.sort_by,
                'desc': this.desc,
                'search': this.search ? {'name': this.search} : {}
            });
        },
        handleSortBy(type) {
            this.desc = !this.desc;
            this.sort_by = type;

            this.handleChangePage();
        },
        handleSearchChange(search) {
            this.search = search;
            this.handleChangePage();
        },
        handleCreateFloor() {
            this.$store.dispatch('floors/resetSingle')
            this.$store.dispatch('modals/open', 'floor-save');
        },
        handleEditFloor(floor) {
            this.$store.dispatch('floors/setSingle', floor);
            this.$store.dispatch('modals/open', 'floor-save');
        },
        handleManageFloor(floor) {
            this.$router.push({name: 'floor-show', params: {id: floor.id}})
        },
        handleDragEnd(items) {
            this.$store.dispatch('floors/move', { movedFields: items });
        },
        handleEditSettings() {
            this.$store.dispatch('modals/open', 'building_settings-save')
        },
        handleSync(building) {
            this.$store.dispatch('buildings/sync', {id: building.id});
        }
    },
    components: {
        PageHeader,
        Loader,
        LangSwitcher,
        Search,
        List,
        Pagination
    }
}
</script>
